import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CHECKLIST_ITEM, DESCRIPTION, TITLE, IMPORTANCE_FLAG } from "./consts";
import { useNavigate, useParams } from "react-router-dom";
import { formValues, schema } from "./validations";
import {
  capitalize,
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAddQualityChecklistItemMutation,
  useAddQualityChecklistMutation,
  useDeleteQualityChecklistItemMutation,
  useUpdateQualityChecklistItemMutation,
  useGetQualityChecklistById,
  useGetQualityChecklistItemList,
  useUpdateQualityChecklistMutation,
} from "@/services";
import { toast } from "@/components";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useEffect, useState } from "react";

const useAddEdit = () => {
  const { id, action, tabRowId, tabRowAction } = useParams();
  const isViewCategory = tabRowAction == "view";
  const isViewProject = action == "view";
  const [deletedItemIds, setDeletedItemIds] = useState<string[]>([])
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = action == "edit";
  const isView = isViewCategory || isViewProject;

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [TITLE]: "",
      [CHECKLIST_ITEM]: [{name: "", [IMPORTANCE_FLAG]: ""}],
      [DESCRIPTION]: "",
    },
  });

  const projectData = useSelector(
    (state: RootState) => state.currentData.projectData
  );

  const requiredFields = getRequiredFields(schema);

  const navigateUrl = id
    ? `/${routes.admin}/${routes.projectOverview}/${id}/${action}/${routes.qualityChecklist}`
    : `/${routes.admin}/${routes.projectOverview}/${routes.add}/${routes.qualityChecklist}`;

  const breadcrumbData = [
    {
      link: navigateUrl,
      name: `Checklist for Quality`,
    },
    {
      link: "",
      name: tabRowAction ? capitalize(tabRowAction || "") : "Add",
    },
  ];

  const title = tabRowId ? `${capitalize(tabRowAction || '')} Checklist for Quality` : 'Add Checklist for Quality'

  const onBackClick = () => {
    navigate(navigateUrl);
  };

  const {data: checklistData, isLoading: isLoadingChecklistData} = useGetQualityChecklistById({
    id: tabRowId as string,
    queryParams: {
      enabled: !!tabRowId
    }
  })

  const {data: checklistItems, isLoading: isLoadingChecklistItems} = useGetQualityChecklistItemList({
    checklistId: tabRowId as string,
    queryParams: {
      enabled: !!tabRowId
    }
  })

  useEffect(() => {
    if (checklistData?.data) {
      form.setValue(TITLE, checklistData?.data?.[TITLE] || '')
      form.setValue(DESCRIPTION, checklistData?.data?.[DESCRIPTION] || '')
    }
  }, [checklistData]);

  useEffect(() => {
    if (checklistItems?.data) {
      form.setValue(CHECKLIST_ITEM, checklistItems?.data)
    }
  }, [checklistItems])
  
  const { mutateAsync: addItemInfo, isPending: isAddItemPending } =
    useAddQualityChecklistItemMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
    });

  const { mutateAsync: updateItemInfo, isPending: isUpdateItemPending } =
    useUpdateQualityChecklistItemMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
    });

  const { mutateAsync: deleteItemInfo, isPending: isDeleteItemPending } =
    useDeleteQualityChecklistItemMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
    });

  const { mutate: addInfo, isPending: isAddPending } =
    useAddQualityChecklistMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: async (data: any) => {
        try {
          const additionalPromises: Promise<any>[] = [];

          form.watch(CHECKLIST_ITEM)?.map((val: any) => {
            additionalPromises.push(
              addItemInfo({
                checklistId: data?.data?.data?.taskQualityChecklistId,
                data: {
                  ...val,
                },
              })
            );
          });

          await Promise.all(additionalPromises);

          toast({
            message: "Checklist Added Successfully",
            type: "success",
          });

          queryClient.invalidateQueries({
            queryKey: ["quality-checklist", projectData?.projectId],
          });
          onBackClick();
        } catch (error) {
          console.error("Error in post-success APIs:", error);
          toast({
            message: "Something went wrong while processing additional data.",
            type: "error",
          });
        }
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateQualityChecklistMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: async (data: any) => {
        try {
          const additionalPromises: Promise<any>[] = [];

          form.watch(CHECKLIST_ITEM)?.map((val: any) => {
            if (!val.taskQualityChecklistItemId) {
              additionalPromises.push(
                addItemInfo({
                  checklistId: data?.data?.data?.taskQualityChecklistId,
                  data: {
                     ...val,
                  },
                })
              );
            } else {
              additionalPromises.push(
                updateItemInfo({
                  checklistId: data?.data?.data?.taskQualityChecklistId,
                  itemId: val.taskQualityChecklistItemId,
                  data: {
                    name: val.name,
                    importanceFlag: val.importanceFlag,
                  },
                })
              );
            }
          });

          deletedItemIds?.map((val: any) => {
            additionalPromises.push(
              deleteItemInfo({id: val, checklistId: tabRowId as string})
            )
          })

          await Promise.all(additionalPromises);

          toast({
            message: "Checklist Updated Successfully",
            type: "success",
          });

          queryClient.invalidateQueries({
            queryKey: ["quality-checklist", projectData?.projectId],
          });
          onBackClick();
        } catch (error) {
          console.error("Error in post-success APIs:", error);
          toast({
            message: "Something went wrong while processing additional data.",
            type: "error",
          });
        }
      },
    });

  const onSubmit = (data: formValues) => {
    let _data = setEmptyFieldsToNull(data);
    _data = removeEmptyFields(_data);

    if (tabRowId) {
      updateInfo({id: tabRowId, data: { ..._data, projectId: projectData?.projectId } });
    } else {
      addInfo({ data: { ..._data, projectId: projectData?.projectId } });
    }
  };

  const onAddMoreTask = () => {
    form.clearErrors();
    form.setValue(CHECKLIST_ITEM, [...form.getValues()[CHECKLIST_ITEM], {name: "", [IMPORTANCE_FLAG]: ""}]);
  };

  const onDeleteTask = (idx: number) => () => {
    const taskQualityChecklistItemId = (form.getValues()[CHECKLIST_ITEM][idx] as any)?.taskQualityChecklistItemId

    if (taskQualityChecklistItemId) {
      setDeletedItemIds((prev: any) => [...prev, taskQualityChecklistItemId])
    }

    form.setValue(CHECKLIST_ITEM, [
      ...form
        .getValues()
        [CHECKLIST_ITEM]?.filter((_: any, index: number) => idx != index),
    ]);
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
    onAddMoreTask,
    onDeleteTask,
    isEdit,
    isAddItemPending: isAddItemPending || isUpdateItemPending,
    isView,
    isDeleteItemPending,
    isUpdatePending,
    isLoadingChecklistData,
    isLoadingChecklistItems,
    title
  };
};

export default useAddEdit;
